import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useProjects } from '@/hooks/useProjects';
import { useLocation } from 'react-router-dom';
import { SummaryStats } from '@/components/SummaryStats';
import { MonthlyLineChart } from '@/components/MonthlyLineChart';
import { ProjectBreakdown } from '@/components/ProjectBreakdown';
import { TimeRecordsTable } from '@/components/TimeRecordsTable';
import { TimeRecordModal } from '@/components/TimeRecordModal';
import { ExportOptionsModal } from '@/components/ExportOptionsModal';
import {
  fetchTimeRecordsForPeriod,
  calculateMonthlyStats,
  calculateProjectStats,
  exportToCSV,
  type MonthlyStats,
  type ProjectStats,
} from '@/lib/reports';
import { generatePDFReport } from '@/lib/pdf';
import { calculateProjectBilling } from '@/lib/billing';
import {
  updateTimeRecordDetails,
  deleteTimestamp,
  addTimestamp,
} from '@/lib/timeTracking';
import type { TimeRecord } from '@/types';
import { Calendar, Plus } from 'lucide-react';
import { Timestamp } from 'firebase/firestore';

export const SummaryPage = () => {
  const { user } = useAuth();
  const { projects } = useProjects('all');
  const location = useLocation();
  
  const [startMonth, setStartMonth] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 2);
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
  });
  
  const [endMonth, setEndMonth] = useState(() => {
    const date = new Date();
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
  });

  const [tableMonth, setTableMonth] = useState(() => {
    const date = new Date();
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
  });

  const [loading, setLoading] = useState(false);
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats[]>([]);
  const [projectStats, setProjectStats] = useState<ProjectStats[]>([]);
  const [tableRecords, setTableRecords] = useState<TimeRecord[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<TimeRecord | undefined>(undefined);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportType, setExportType] = useState<'csv' | 'pdf'>('csv');

  // グラフデータの取得
  const fetchGraphData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const [startYear, startMonthNum] = startMonth.split('-').map(Number);
      const [endYear, endMonthNum] = endMonth.split('-').map(Number);
      
      const startDate = new Date(startYear, startMonthNum - 1, 1);
      const endDate = new Date(endYear, endMonthNum, 0, 23, 59, 59);

      const records = await fetchTimeRecordsForPeriod(user.uid, startDate, endDate);
      const stats = calculateMonthlyStats(records, startDate, endDate);
      setMonthlyStats(stats);

      const projStats = calculateProjectStats(records, projects);
      
      // 各プロジェクトの報酬を計算
      const projStatsWithAmount = projStats.map((stat) => {
        const project = projects.find((p) => p.id === stat.projectId);
        if (!project || !project.contractType) {
          return stat;
        }
        
        const projectRecords = records.filter((r) => r.projectId === stat.projectId);
        const billing = calculateProjectBilling(project, projectRecords);
        
        return {
          ...stat,
          totalAmount: billing.totalAmount,
        };
      });
      
      setProjectStats(projStatsWithAmount);
    } catch (error) {
      console.error('Failed to fetch graph data:', error);
    } finally {
      setLoading(false);
    }
  };

  // テーブルデータの取得
  const fetchTableData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const [year, month] = tableMonth.split('-').map(Number);
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0, 23, 59, 59);

      const records = await fetchTimeRecordsForPeriod(user.uid, startDate, endDate);
      setTableRecords(records);
    } catch (error) {
      console.error('Failed to fetch table data:', error);
    } finally {
      setLoading(false);
    }
  };

  // フィルター済みレコード
  const filteredRecords = selectedProjectId === 'all'
    ? tableRecords
    : tableRecords.filter(r => r.projectId === selectedProjectId);

  useEffect(() => {
    fetchGraphData();
  }, [user, startMonth, endMonth, projects]);

  useEffect(() => {
    fetchTableData();
  }, [user, tableMonth]);

  // ハッシュに基づいてスクロール
  useEffect(() => {
    if (location.hash === '#records') {
      setTimeout(() => {
        const element = document.getElementById('records');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, [location.hash]);

  const handleExportCSV = () => {
    if (filteredRecords.length === 0) {
      alert('エクスポートするデータがありません');
      return;
    }
    setExportType('csv');
    setIsExportModalOpen(true);
  };

  const handleExportPDF = async () => {
    if (filteredRecords.length === 0) {
      alert('エクスポートするデータがありません');
      return;
    }
    setExportType('pdf');
    setIsExportModalOpen(true);
  };

  const handleExport = async (selectedColumns: string[], selectedProject?: string) => {
    const filename = `timerecords_${tableMonth}.${exportType}`;

    if (exportType === 'csv') {
      exportToCSV(filteredRecords, filename, selectedColumns);
    } else {
      // PDF export logic
      // PDFの日本語フォント問題のため、CSV推奨メッセージを表示
      const confirmExport = window.confirm(
        'PDFでは日本語が正しく表示されない可能性があります。\n\nCSVエクスポートを推奨します。\n\nそれでもPDFでエクスポートしますか？'
      );
      
      if (!confirmExport) {
        return;
      }

      try {
        const [year, month] = tableMonth.split('-').map(Number);
        const startDate = new Date(year, month - 1, 1);
        const endDate = new Date(year, month, 0, 23, 59, 59);

        // Filter records by selected project if specified
        const recordsToExport = selectedProject
          ? filteredRecords.filter((r) => r.projectId === selectedProject)
          : filteredRecords;

        await generatePDFReport(
          recordsToExport,
          projects,
          startDate,
          endDate,
          `Working Report ${tableMonth}`
        );
      } catch (error) {
        console.error('PDF export error:', error);
        alert('PDFの生成に失敗しました。CSVエクスポートをお試しください。');
      }
    }
  };

  // サマリー統計の計算
  const totalMinutes = tableRecords.reduce((sum, r) => sum + (r.totalMinutes || 0), 0);
  const totalHours = Math.round(totalMinutes / 60 * 10) / 10;
  const workDays = new Set(
    tableRecords.map((r) => r.clockInTime.toDate().toDateString())
  ).size;
  const avgHoursPerDay = workDays > 0 ? Math.round((totalHours / workDays) * 10) / 10 : 0;

  // 🆕 報酬額の計算
  const calculateTotalReward = () => {
    let totalReward = 0;
    
    // プロジェクトごとにグループ化
    const projectGroups = new Map<string, TimeRecord[]>();
    filteredRecords.forEach(record => {
      const projectId = record.projectId || 'unassigned';
      if (!projectGroups.has(projectId)) {
        projectGroups.set(projectId, []);
      }
      projectGroups.get(projectId)!.push(record);
    });

    // 各プロジェクトの報酬を計算
    projectGroups.forEach((records, projectId) => {
      const project = projects.find(p => p.id === projectId);
      if (!project || !project.contractType) return;

      const result = calculateProjectBilling(project, records);
      totalReward += result.totalAmount;
    });

    return totalReward;
  };

  const totalReward = calculateTotalReward();

  if (loading && monthlyStats.length === 0) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* ページタイトル */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent">
          サマリー・レポート
        </h1>
        <p className="text-gray-600 mt-2">稼働実績の集計とレポート出力</p>
      </div>

      {/* グラフ期間選択 */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex items-center gap-2 text-gray-700">
            <Calendar className="w-5 h-5" />
            <span className="font-medium">グラフ表示期間:</span>
          </div>
          <div className="flex items-center gap-4">
            <input
              type="month"
              value={startMonth}
              onChange={(e) => setStartMonth(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <span className="text-gray-500">〜</span>
            <input
              type="month"
              value={endMonth}
              onChange={(e) => setEndMonth(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* 月次折れ線グラフ */}
      <MonthlyLineChart data={monthlyStats} />

      {/* プロジェクト別統計 */}
      <ProjectBreakdown stats={projectStats} />

      {/* テーブル期間選択 */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 text-gray-700">
            <Calendar className="w-5 h-5" />
            <span className="font-medium">詳細表示月:</span>
          </div>
          <input
            type="month"
            value={tableMonth}
            onChange={(e) => setTableMonth(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          
          {/* プロジェクトフィルター */}
          <div className="flex items-center gap-2 text-gray-700 ml-auto">
            <span className="font-medium">プロジェクトフィルター:</span>
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">すべてのプロジェクト</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.code ? `${project.code} - ` : ''}{project.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* 統計サマリー */}
      <SummaryStats
        totalHours={totalHours}
        totalMinutes={totalMinutes}
        workDays={workDays}
        avgHoursPerDay={avgHoursPerDay}
        totalRecords={tableRecords.length}
        totalReward={totalReward}
      />

      {/* 新規追加ボタン */}
      <div className="mb-4">
        <button
          onClick={() => {
            setEditingRecord(undefined);
            setIsModalOpen(true);
          }}
          className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-lg font-medium hover:from-indigo-600 hover:to-indigo-700 transition-all duration-200 shadow-md flex items-center gap-2"
          disabled={isProcessing}
        >
          <Plus className="w-5 h-5" />
          稼働記録を追加
        </button>
      </div>

      {/* 詳細テーブル */}
      <div id="records">
        <TimeRecordsTable 
          records={filteredRecords}
          projects={projects}
          onExportCSV={handleExportCSV} 
          onExportPDF={handleExportPDF}
          onEdit={(record) => {
            setEditingRecord(record);
            setIsModalOpen(true);
          }}
          onDelete={async (recordId) => {
            if (!user) return;
            setIsProcessing(true);
            try {
              await deleteTimestamp(user.uid, recordId);
              // テーブルデータとグラフデータを再取得
              await Promise.all([fetchTableData(), fetchGraphData()]);
              alert('削除しました');
            } catch (error) {
              console.error('Delete error:', error);
              alert('削除に失敗しました');
            } finally {
              setIsProcessing(false);
            }
          }}
        />
      </div>

      {/* 編集・追加モーダル */}
      <TimeRecordModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingRecord(undefined);
        }}
        record={editingRecord}
        projects={projects}
        isProcessing={isProcessing}
        onSubmit={async (data) => {
          if (!user) return;
          setIsProcessing(true);
          try {
            if (editingRecord) {
              // 編集モード
              // data.clockInTime/clockOutTime は Date オブジェクト
              const clockInDate = data.clockInTime instanceof Date 
                ? data.clockInTime 
                : (data.clockInTime as any).toDate?.() || new Date(data.clockInTime as any);
              const clockOutDate = data.clockOutTime 
                ? (data.clockOutTime instanceof Date 
                    ? data.clockOutTime 
                    : (data.clockOutTime as any).toDate?.() || new Date(data.clockOutTime as any))
                : null;

              await updateTimeRecordDetails(
                user.uid,
                editingRecord.id,
                {
                  clockInTime: clockInDate,
                  clockOutTime: clockOutDate,
                  projectId: data.projectId,
                  project: data.project,
                  categories: data.categories,
                  memo: data.memo,
                },
                user.email || 'unknown'
              );
              alert('更新しました');
            } else {
              // 新規追加モード
              const clockInDate = data.clockInTime instanceof Date 
                ? data.clockInTime 
                : new Date(data.clockInTime as any);
              const clockOutDate = data.clockOutTime 
                ? (data.clockOutTime instanceof Date 
                    ? data.clockOutTime 
                    : new Date(data.clockOutTime as any))
                : null;

              // totalMinutesを計算（渡されていない、または0の場合）
              let totalMinutes = data.totalMinutes || 0;
              if (clockOutDate && totalMinutes === 0) {
                totalMinutes = Math.floor(
                  (clockOutDate.getTime() - clockInDate.getTime()) / 60000
                );
              }

              await addTimestamp(user.uid, {
                clockInTime: Timestamp.fromDate(clockInDate),
                clockOutTime: clockOutDate ? Timestamp.fromDate(clockOutDate) : null,
                projectId: data.projectId,
                project: data.project,
                categories: data.categories || [],
                memo: data.memo || '',
                totalMinutes,
                status: clockOutDate ? 'completed' : 'active',
                recordType: 'manual', // 🆕 手動入力
              });
              alert('追加しました');
            }
            // テーブルデータとグラフデータを再取得
            await Promise.all([fetchTableData(), fetchGraphData()]);
            setIsModalOpen(false);
            setEditingRecord(undefined);
          } catch (error) {
            console.error('Submit error:', error);
            alert(editingRecord ? '更新に失敗しました' : '追加に失敗しました');
          } finally {
            setIsProcessing(false);
          }
        }}
      />

      {/* エクスポートオプションモーダル */}
      <ExportOptionsModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExport}
        exportType={exportType}
        projects={projects}
      />
    </div>
  );
};
